/**
 * Formats d'affichage.
 *
 * FCFA entier sans décimale, dates au fuseau du Gabon (UTC+1, sans heure d'été).
 */
import type { EnvMessage, StatutMessage } from '../api/client';

const FORMAT_NOMBRE = new Intl.NumberFormat('fr-FR');

/** Espaces fines insécables remplacées par des espaces simples, plus lisibles ici. */
const nombre = (v: number) =>
  // Intl place des espaces fines insécables ; on les ramène à l'espace simple,
  // plus lisible dans un tableau dense et copiable sans surprise.
  FORMAT_NOMBRE.format(v).replace(/[\u202f\u00a0]/g, ' ');

export const formaterNombre = nombre;

/** `12450` → `12 450 FCFA`. Jamais de décimale : le franc n'a pas de sous-unité. */
export const formaterFcfa = (xaf: number) => `${nombre(xaf)} FCFA`;

/** Montant signé du grand livre : `+ 100 000 FCFA` ou `− 25 FCFA`. */
export const formaterMontantSigne = (xaf: number) =>
  `${xaf < 0 ? '−' : '+'} ${nombre(Math.abs(xaf))} FCFA`;

const FUSEAU = 'Africa/Libreville';

const FORMAT_LONG = new Intl.DateTimeFormat('fr-FR', {
  timeZone: FUSEAU,
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const FORMAT_COMPACT = new Intl.DateTimeFormat('fr-FR', {
  timeZone: FUSEAU,
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const FORMAT_HEURE = new Intl.DateTimeFormat('fr-FR', {
  timeZone: FUSEAU,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

export const formaterDateLongue = (iso: string) => FORMAT_LONG.format(new Date(iso));
export const formaterHeure = (iso: string) => FORMAT_HEURE.format(new Date(iso));

/** Moins d'une heure : relatif. Au-delà : date compacte. */
export function formaterDate(iso: string): string {
  const date = new Date(iso);
  const ecartMinutes = Math.round((Date.now() - date.getTime()) / 60_000);
  if (ecartMinutes < 1) return "à l'instant";
  if (ecartMinutes < 60) return `il y a ${ecartMinutes} min`;
  return FORMAT_COMPACT.format(date);
}

/**
 * Date civile absolue, en heure du Gabon.
 *
 * Sur une pièce comptable, « à l'instant » ne veut rien dire : OHADA exige une
 * date d'émission et une échéance datées. Ce formateur ne doit jamais être
 * remplacé par `formaterDate`, qui est relatif.
 */
export function formaterDateCivile(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: FUSEAU,
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

/** `2026-08` → `août 2026`. */
export function formaterMois(aaaaMm: string): string {
  const [an, mois] = aaaaMm.split('-');
  const date = new Date(Date.UTC(Number(an), Number(mois) - 1, 15));
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: FUSEAU,
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/** `+24166001242` → `+241 66 00 12 42`. */
export function formaterNumero(e164: string): string {
  const chiffres = e164.replace(/\D/g, '');
  if (!chiffres.startsWith('241') || chiffres.length !== 11) return e164;
  const paires = chiffres.slice(3).match(/.{1,2}/g) ?? [];
  return `+241 ${paires.join(' ')}`;
}

/**
 * Opérateur déduit du premier chiffre d'abonné, depuis la migration à 9 chiffres
 * de juillet 2019 : 7 Airtel, 6 Moov, 1 ligne fixe.
 * À confirmer sur le plan de numérotation ARCEP.
 */
export function operateurDe(e164: string): string {
  const abonne = e164.replace(/\D/g, '').replace(/^241/, '');
  if (abonne.startsWith('7')) return 'Airtel';
  if (abonne.startsWith('6')) return 'Moov';
  if (abonne.startsWith('1')) return 'Fixe';
  return '—';
}

// --- Statuts ----------------------------------------------------------------

type TonPuce = 'ok' | 'attente' | 'neutre' | 'succes' | 'echec';

const STATUTS: Record<StatutMessage, { libelle: string; ton: TonPuce }> = {
  queued: { libelle: 'En file', ton: 'neutre' },
  submitted: { libelle: 'Soumis', ton: 'ok' },
  sent: { libelle: 'Émis', ton: 'ok' },
  delivered: { libelle: 'Délivré', ton: 'succes' },
  undelivered: { libelle: 'Non remis', ton: 'echec' },
  expired: { libelle: 'Expiré', ton: 'echec' },
  rejected: { libelle: 'Rejeté', ton: 'echec' },
  opted_out: { libelle: 'Filtré (STOP)', ton: 'neutre' },
};

export const aspectStatut = (statut: StatutMessage) =>
  STATUTS[statut] ?? { libelle: statut, ton: 'neutre' as TonPuce };

// --- Facturation ------------------------------------------------------------

/**
 * Ce qui a réellement été prélevé pour un message.
 *
 * Le montant porté par un message est son **coût calculé à l'acceptation**, et
 * non une écriture au grand livre. Le débit n'a lieu qu'à la transition vers
 * `submitted` ; `undelivered` et `expired` donnent lieu à un recrédit immédiat ;
 * un message rejeté ou filtré par la liste STOP n'est jamais débité. La règle
 * fait autorité côté serveur (`lifecycle.service.ts`), elle est reflétée ici.
 *
 * Afficher ce montant sans rien dire revenait à annoncer en gras 25 FCFA sur un
 * message rejeté que personne n'a payés — inacceptable sur une plateforme dont
 * les soldes se dérivent du grand livre.
 */
export type EtatFacturation = 'facture' | 'non_facture' | 'rembourse' | 'a_venir';

const FACTURATION: Record<StatutMessage, EtatFacturation> = {
  queued: 'a_venir',
  submitted: 'facture',
  sent: 'facture',
  delivered: 'facture',
  undelivered: 'rembourse',
  expired: 'rembourse',
  rejected: 'non_facture',
  opted_out: 'non_facture',
};

const MENTIONS: Record<EtatFacturation, string | null> = {
  facture: null,
  a_venir: 'à débiter si l’opérateur l’accepte',
  rembourse: 'débité puis remboursé',
  non_facture: 'non facturé',
};

/** En bac à sable rien n'est jamais débité, quel que soit le statut. */
export function etatFacturation(statut: StatutMessage, environnement: EnvMessage): EtatFacturation {
  if (environnement === 'test') return 'non_facture';
  return FACTURATION[statut] ?? 'a_venir';
}

/** Mention à afficher à côté du montant, ou `null` quand il a bien été pris. */
export const mentionFacturation = (etat: EtatFacturation) => MENTIONS[etat];

const TYPES_ECRITURE: Record<string, string> = {
  topup: 'Recharge',
  message_debit: 'Envoi',
  message_refund: 'Recrédit',
  adjustment: 'Ajustement',
  reversal: 'Annulation',
};

export const libelleEcriture = (type: string) => TYPES_ECRITURE[type] ?? type;

export const libelleEncodage = (encodage: 'gsm7' | 'ucs2') =>
  encodage === 'gsm7' ? 'GSM-7' : 'Unicode';

/** Initiales pour l'avatar de l'en-tête. */
export function initiales(nom: string | null, email: string): string {
  const source = (nom ?? email).trim();
  const mots = source.split(/[\s@._-]+/).filter(Boolean);
  return ((mots[0]?.[0] ?? '') + (mots[1]?.[0] ?? '')).toUpperCase() || 'IN';
}
